// src/components/pages/MainPage/MainPage.tsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../services/useUserData';
import { ObfuscatedStrings } from '../../services/obfyscatedStrings';
import { useAppStore } from '../../store/useAppStore';
import Loader from '../../widgets/Loader/Loader';
import BackgroundCanvas from '../../widgets/canvas/BackgroundCanvas';
import { ActionIcons } from '../../assets/images/action';
import './MainPage.scss';

const getDaysLeft = (expiresAt: string): number => {
    const expireDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expireDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const MainPage: React.FC = () => {
    const navigate = useNavigate();
    const { telegramId, loading: userLoading } = useUserData();
    const { devices, loadDevices, isLoading: storeLoading } = useAppStore();
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [hasDevices, setHasDevices] = useState<boolean>(false);
    const [isDataReady, setIsDataReady] = useState<boolean>(false);
    const [showContent, setShowContent] = useState<boolean>(false);

    // Реф для отслеживания, что данные уже загружены
    const dataLoadedRef = useRef<boolean>(false);

    const isLoading = userLoading || storeLoading || !isDataReady;

    // Загрузка устройств
    useEffect(() => {
        const loadDevicesData = async () => {
            if (!telegramId || dataLoadedRef.current) return;
            dataLoadedRef.current = true;

            const devicesData = await loadDevices(telegramId);
            if (devicesData?.success) {
                setHasDevices(devicesData.total > 0);

                const hasExpiringSoon = devicesData.devices.some(device => {
                    const daysLeft = getDaysLeft(device.expires_at);
                    return daysLeft <= 7 && daysLeft > 0;
                });

                setShowAlert(hasExpiringSoon);
            }

            setIsDataReady(true);
            setTimeout(() => {
                setShowContent(true);
            }, 50);
        };

        loadDevicesData();
    }, [telegramId, loadDevices]);

    // Настройка Telegram WebApp (один раз)
    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }
    }, []);

    const handleRenewClick = useCallback(() => {
        navigate('/payment-devices');
    }, [navigate]);

    const handleConnectClick = useCallback(() => {
        navigate('/connect');
    }, [navigate]);

    const handlePaymentClick = useCallback(() => {
        navigate('/payment');
    }, [navigate]);

    // Показываем лоадер пока данные не готовы
    if (isLoading) {
        return (
            <div className="main-page">
                <BackgroundCanvas />
                <div className="main-page__content loading">
                    <div className="loader"><Loader /></div>
                </div>
            </div>
        );
    }

    return (
        <div className="main-page">
            <BackgroundCanvas />
            <div className={`main-page__content ${showContent ? 'visible' : 'hidden'}`}>
                <div className="main-page__wrapper">
                    <div className="main-page__bottom">
                        {showAlert && (
                            <div className="subscription-alert warning fade-in">
                                <div className="alert-icon">
                                    {ActionIcons.alertClock()}
                                </div>
                                <span className="alert-text">
                                    Ваша подписка скоро закончится! Продлите сейчас, чтобы не потерять доступ.
                                </span>
                            </div>
                        )}

                        <div className="main-page__toolbar">
                            <button
                                onClick={handleConnectClick}
                                className="fade-in"
                                style={{ animationDelay: '0.1s' }}
                            >
                                <div className="icon"><ActionIcons.connect /></div>
                                <span>Подключить</span>
                                <small>{ObfuscatedStrings.getCodeConfigurationDefult()}</small>
                            </button>

                            <button
                                onClick={handlePaymentClick}
                                className="fade-in"
                                style={{ animationDelay: '0.1s' }}
                            >
                                <div className="icon"><ActionIcons.subscription /></div>
                                <span>Купить</span>
                                <small>от 129₽</small>
                            </button>

                            {hasDevices && (
                                <button
                                    onClick={handleRenewClick}
                                    className={`fade-in ${showAlert ? 'renew-button warning' : ''}`}
                                    style={{ animationDelay: '0.1s' }}
                                >
                                    <div className={`icon renew-icon ${showAlert ? 'warning' : ''}`}>
                                        {ActionIcons.renew()}
                                    </div>
                                    <span className={showAlert ? 'warning' : ''}>Продлить</span>
                                    <small className={showAlert ? 'warning' : ''}>подписку</small>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MainPage;